import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React from "react";

interface MiniStatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  color: string;
  loading?: boolean;
}

const MiniStatCard = ({
  title,
  value,
  icon,
  color,
  loading = false,
}: MiniStatCardProps) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          position: "relative",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Skeleton variant="text" width={80} height={24} animation="wave" />
          <Skeleton variant="text" width={60} height={28} animation="wave" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: "all 0.2s ease",
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          backgroundColor: color,
          transition: "all 0.2s ease",
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              color: theme.palette.text.secondary,
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box
              sx={{
                color: color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.25rem",
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.palette.text.primary,
          }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MiniStatCard;
